/**
 * PhaseStack — non-clickable phase-record stack (.stack P1–P10).
 *
 * Faithful port of the canonical wireframe's `.stack` / `.pr` markup
 * (docs/wireframes/celestia/03-screen.html, lines ~383–419).
 *
 * Each phase card is data-testid="celestia-phase-record-${phaseId}" with
 * data-phase-status = "done" | "cur" | "future". Content in .pl2 varies
 * per phase type: dice glyphs (P2/P7), pslots (P3/P4/P8), captain claim
 * (P2b/P5), or descriptive text (P1/P9/P10).
 */

import type { PhaseContext, ViewerState } from "@cloudnest/celestia-engine";
import { DICE_FACE_ICON } from "../components/CelestiaIconSprite";
import {
  PHASE_BADGE,
  PHASE_ORDER,
  seatLabel,
  seatVar,
} from "../screen-constants";

const PHASE_NM: Readonly<Record<string, string>> = {
  AwaitSoloCaptainChoice: "단독 선장",
  AwaitRoll: "주사위",
  AwaitCaptainClaim: "선장 공표",
  AwaitPassengerDecision: "승객 결정",
  AwaitEjectionWindow: "강제 하차",
  AwaitCaptainDeclaration: "선장 선언",
  AwaitReactionWindow: "반응",
  AwaitResolution: "장비 지불",
  AwaitTreasureChoice: "보물 선택",
  EndOfLeg: "여정",
  EndOfJourney: "새 모험",
};

type PhaseStatus = "done" | "cur" | "future";

const DICE_FACE_BG: Readonly<Record<string, string>> = {
  fog: "var(--cn-celestia-event-fog)",
  lightning: "var(--cn-celestia-event-lightning)",
  birds: "var(--cn-celestia-event-birds)",
  pirate: "var(--cn-celestia-event-pirate)",
  blank: "blank",
  fiftyFifty: "var(--cn-celestia-event-pirate)",
};

interface Props {
  readonly viewerState: ViewerState;
  readonly myPlayerId: string | null;
}

function MiniDie({ face }: { readonly face: string }) {
  const bg = DICE_FACE_BG[face] ?? "#fff";
  const isBlank = face === "blank";
  const isDark = face === "lightning";
  const iconId =
    DICE_FACE_ICON[face as keyof typeof DICE_FACE_ICON] ?? "ic-blank";
  return (
    <span
      className={
        isBlank ? "mini-die blank" : isDark ? "mini-die dark" : "mini-die"
      }
      style={isBlank ? undefined : ({ "--db": bg } as React.CSSProperties)}
    >
      <svg className="ico" aria-hidden="true">
        <use href={`#${iconId}`} />
      </svg>
    </span>
  );
}

function PlayerSlot({
  seat,
  status,
  showAct,
}: {
  readonly seat: number;
  readonly status: "stay" | "leave" | "pass" | "pick" | "na";
  readonly showAct?: boolean;
}) {
  const mk: Record<string, string> = {
    stay: "✓",
    leave: "✗",
    pass: "·",
    pick: "◆",
  };
  return (
    <span
      className={showAct ? `ps ${status} act` : `ps ${status}`}
      style={{ background: seatVar(seat) }}
    >
      {seatLabel(seat)}
      {status !== "na" && <span className="mk">{mk[status]}</span>}
    </span>
  );
}

function PhaseContent({
  phaseId,
  viewerState,
  status,
}: {
  readonly phaseId: string;
  readonly viewerState: ViewerState;
  readonly status: PhaseStatus;
}) {
  const aircraft = viewerState.vessels.find((v) => v.kind === "aircraft");
  const captainPlayerId = aircraft?.captainPlayerId ?? "";
  const _passengers = aircraft?.passengers ?? [];
  const dice = aircraft?.currentDice ?? null;
  const ctx: PhaseContext = viewerState.currentPhase.context;
  const players = viewerState.players;

  if (status === "future") {
    // pl2 hidden via CSS .pr.future .pl2 { visibility: hidden }
    return null;
  }

  // P1 — solo captain
  if (phaseId === "AwaitSoloCaptainChoice") {
    return <span>착륙 또는 계속</span>;
  }

  // P2 — roll: show dice glyphs if available
  if (phaseId === "AwaitRoll") {
    if (dice && dice.length > 0) {
      return (
        <>
          {dice.map((face, i) => (
            <MiniDie key={`${face}-${i}`} face={face} />
          ))}
        </>
      );
    }
    return <span className="cel-muted">대기 중</span>;
  }

  // P2b — captain claim
  if (phaseId === "AwaitCaptainClaim") {
    const captain = players.find((p) => p.playerId === captainPlayerId);
    const captainSeat = captain?.seat ?? 0;
    return (
      <>
        <span className="ps" style={{ background: seatVar(captainSeat) }}>
          {seatLabel(captainSeat)}
          <span className="hat">
            <svg className="ico" aria-hidden="true">
              <use href="#ic-caphat" />
            </svg>
          </span>
        </span>
        <span
          className="nm"
          style={{ color: "var(--cn-celestia-deadline-ring)" }}
        >
          FAIL 공표
        </span>
        <span className="cel-muted">· 거짓 가능</span>
      </>
    );
  }

  // P3 — passenger decision: show pslots
  if (phaseId === "AwaitPassengerDecision") {
    const activeId =
      ctx.phase === "AwaitPassengerDecision" ? ctx.activePassengerId : null;
    return (
      <div className="pslots">
        {players.map((p) => {
          const isCaptain = p.playerId === captainPlayerId;
          const isDc = p.connectionStatus === "disconnected";
          if (isCaptain || isDc || !p.isOnAircraft) {
            return <PlayerSlot key={p.playerId} seat={p.seat} status="na" />;
          }
          const isAct = p.playerId === activeId;
          return (
            <PlayerSlot
              key={p.playerId}
              seat={p.seat}
              status="stay"
              showAct={isAct}
            />
          );
        })}
      </div>
    );
  }

  // P4 — ejection window: show pass slots for eligible passengers
  if (phaseId === "AwaitEjectionWindow") {
    return (
      <div className="pslots">
        {players.map((p) => {
          const isCaptain = p.playerId === captainPlayerId;
          const isDc = p.connectionStatus === "disconnected";
          if (isCaptain || isDc || !p.isOnAircraft) {
            return <PlayerSlot key={p.playerId} seat={p.seat} status="na" />;
          }
          return <PlayerSlot key={p.playerId} seat={p.seat} status="pass" />;
        })}
      </div>
    );
  }

  // P5 — captain declaration
  if (phaseId === "AwaitCaptainDeclaration") {
    const captain = players.find((p) => p.playerId === captainPlayerId);
    const captainSeat = captain?.seat ?? 0;
    return (
      <>
        <span className="ps" style={{ background: seatVar(captainSeat) }}>
          {seatLabel(captainSeat)}
          <span className="hat">
            <svg className="ico" aria-hidden="true">
              <use href="#ic-caphat" />
            </svg>
          </span>
        </span>
        <span
          className="nm"
          style={{ color: "var(--cn-celestia-deadline-ring)" }}
        >
          선장 · 해결 불가
        </span>
        <span className="cel-muted">· 손패 강제</span>
      </>
    );
  }

  // P6 — reaction window: show pslots, acting captain highlighted
  if (phaseId === "AwaitReactionWindow") {
    const legalActors = viewerState.currentPhase.legalActorSet;
    return (
      <div className="pslots">
        {players.map((p) => {
          const isAct = legalActors.includes(p.playerId);
          const isDc = p.connectionStatus === "disconnected";
          if (isDc || !p.isOnAircraft) {
            return <PlayerSlot key={p.playerId} seat={p.seat} status="na" />;
          }
          return (
            <PlayerSlot
              key={p.playerId}
              seat={p.seat}
              status="stay"
              showAct={isAct}
            />
          );
        })}
      </div>
    );
  }

  // P7 — resolution: show required symbol dice
  if (phaseId === "AwaitResolution") {
    const required = ctx.phase === "AwaitResolution" ? ctx.requiredSymbols : [];
    if (required && required.length > 0) {
      return (
        <>
          {required.map((sym, i) => (
            <MiniDie key={`${sym}-${i}`} face={sym} />
          ))}
        </>
      );
    }
    return (
      <>
        <MiniDie face="fog" />
        <MiniDie face="pirate" />
        <MiniDie face="lightning" />
      </>
    );
  }

  // P8 — treasure choice: show pslots with pick marks
  if (phaseId === "AwaitTreasureChoice") {
    const pendingPickers =
      ctx.phase === "AwaitTreasureChoice" ? ctx.pendingPickers : [];
    return (
      <div className="pslots">
        {players.map((p) => {
          const isDc = p.connectionStatus === "disconnected";
          if (isDc || !p.isOnAircraft) {
            return <PlayerSlot key={p.playerId} seat={p.seat} status="na" />;
          }
          const isPending = pendingPickers?.includes(p.playerId) ?? false;
          return (
            <PlayerSlot
              key={p.playerId}
              seat={p.seat}
              status="pick"
              showAct={isPending}
            />
          );
        })}
      </div>
    );
  }

  // P9 — end of leg
  if (phaseId === "EndOfLeg") {
    return <span>다음 도시 이동 · 선장 교대</span>;
  }

  // P10 — end of journey
  if (phaseId === "EndOfJourney") {
    return <span>전원 장비 +1 · 도시 1 · 선장 교대</span>;
  }

  return null;
}

// Need React import for JSX
import type React from "react";

export function PhaseStack({ viewerState, myPlayerId: _myPlayerId }: Props) {
  const currentPhaseId = viewerState.currentPhase.phase;
  const curIdx = PHASE_ORDER.indexOf(currentPhaseId);

  return (
    <main
      className="stack"
      data-testid="celestia-phase-record-stack"
      aria-label="단계 진행"
    >
      {PHASE_ORDER.map((phaseId, i) => {
        let status: PhaseStatus = "future";
        if (i < curIdx) status = "done";
        else if (i === curIdx) status = "cur";

        const badge = PHASE_BADGE[phaseId] ?? "P?";
        const nm = PHASE_NM[phaseId] ?? phaseId;

        return (
          <div
            key={phaseId}
            className={`pr ${status}`}
            data-testid={`celestia-phase-record-${phaseId}`}
            data-phase-status={status}
          >
            <div className="pl1">
              <span className="sdot" />
              <span className="pn">{badge}</span>
              <span className="nm">{nm}</span>
            </div>
            <div className="pl2">
              <PhaseContent
                phaseId={phaseId}
                viewerState={viewerState}
                status={status}
              />
            </div>
          </div>
        );
      })}
    </main>
  );
}
